import {
  useEffect,
  useRef,
  useContext,
  useCallback,
} from 'react';

import ExternalCoachContext from '../../../../context/ExternalCoachContext';
import useSessionStore from '../../../../../hooks/useSessionStore';
import { loadSession, saveSession, completeSession } from './persistence';
import { serializeCard, restoreCard } from './utils';

// Batches rapid card changes (e.g. typing in the name field) into one
// Firestore write instead of one per keystroke.
const SAVE_DEBOUNCE_MS = 2000;

/**
 * Session recovery for the bulk creator. Restores a previously persisted
 * session once on mount (an accidentally closed browser mid-batch), then
 * keeps the Firestore session doc in sync as the batch progresses.
 * Intentional exits (Done / Cancel / Leave anyway) should call the returned
 * `discardSession`.
 */
const useSessionPersistence = ({
  cards,
  cardsRef,
  setCards,
  autoSave,
  setAutoSave,
  onRestore,
}) => {
  const { coachDoc } = useContext(ExternalCoachContext);
  const coachId = coachDoc?.id;
  // The logged-in user (an AC creating exercises on the coach's behalf, or the
  // coach themselves) — sessions belong to whoever created them.
  const { authUser } = useSessionStore();
  const authUserId = authUser?.uid;

  const restoreAttemptedRef = useRef(false);
  const hadCardsRef = useRef(false); // distinguishes "not started yet" from "coach removed everything"
  const sessionIdRef = useRef(null); // Firestore doc id, known after load or first write
  const lastWrittenRef = useRef(null);
  // Writes are chained so the doc-creating first write always completes before
  // the next one runs — otherwise two rapid writes could both create a doc.
  const writeQueueRef = useRef(Promise.resolve());
  const debounceTimerRef = useRef(null);
  const pendingWriteRef = useRef(null); // latest serialized payload awaiting the debounce timer
  const onRestoreRef = useRef(onRestore);
  onRestoreRef.current = onRestore;

  const cancelPendingWrite = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    pendingWriteRef.current = null;
  }, []);

  const queueWrite = useCallback((payload) => {
    writeQueueRef.current = writeQueueRef.current.then(async () => {
      sessionIdRef.current = await saveSession({
        sessionId: sessionIdRef.current,
        coachId,
        createdBy: authUserId,
        ...payload,
      });
    });
  }, [coachId, authUserId]);

  // Restore once, before any persistence writes.
  useEffect(() => {
    if (restoreAttemptedRef.current) {
      return;
    }
    const restore = async () => {
      const session = await loadSession(coachId, authUserId);
      restoreAttemptedRef.current = true;
      if (session) {
        sessionIdRef.current = session.id;
      }
      // The coach may have started a fresh batch while the session doc loaded
      // — never overwrite live cards with the restored ones.
      if (!session || !session.cards.length || cardsRef.current.length) {
        return;
      }
      const restoredCards = session.cards.map(restoreCard);
      const restoredAutoSave = !!session.autoSave;
      setCards(restoredCards);
      setAutoSave(restoredAutoSave);
      if (onRestoreRef.current) {
        onRestoreRef.current(restoredCards, restoredAutoSave);
      }
    };
    restore();
  }, [coachId, authUserId, cardsRef, setCards, setAutoSave]);

  // Persist on change, debounced. serializeCard excludes volatile fields
  // (File, preview URL, progress), so upload-progress ticks serialize
  // identically and the string comparison skips those writes; rapid changes
  // (typing) coalesce into a single write when the debounce timer fires.
  useEffect(() => {
    if (!restoreAttemptedRef.current) {
      return;
    }
    if (!cards.length) {
      cancelPendingWrite();
      if (hadCardsRef.current) {
        writeQueueRef.current = writeQueueRef.current.then(() => {
          completeSession(sessionIdRef.current);
          sessionIdRef.current = null;
        });
        lastWrittenRef.current = null;
      }
      return;
    }
    hadCardsRef.current = true;
    const serializedCards = cards.map(serializeCard);
    const written = JSON.stringify({ autoSave, cards: serializedCards });
    if (written === lastWrittenRef.current) {
      return;
    }
    lastWrittenRef.current = written;
    pendingWriteRef.current = { autoSave, cards: serializedCards };
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      const payload = pendingWriteRef.current;
      pendingWriteRef.current = null;
      if (payload) {
        queueWrite(payload);
      }
    }, SAVE_DEBOUNCE_MS);
  }, [coachId, authUserId, cards, autoSave, cancelPendingWrite, queueWrite]);

  // Flush a pending debounced write on unmount so navigating away mid-batch
  // doesn't lose the last few seconds of changes from the recovery snapshot.
  useEffect(() => () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (pendingWriteRef.current) {
      queueWrite(pendingWriteRef.current);
      pendingWriteRef.current = null;
    }
  }, [queueWrite]);

  const discardSession = useCallback(() => {
    // Drop any pending debounced write — it must not re-create the doc after
    // the session is marked completed below.
    cancelPendingWrite();
    lastWrittenRef.current = null;
    writeQueueRef.current = writeQueueRef.current.then(() => {
      completeSession(sessionIdRef.current);
      sessionIdRef.current = null;
    });
  }, [cancelPendingWrite]);

  return { discardSession };
};

export default useSessionPersistence;
